import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timeConverter } from '@/utils/time';
import httpsCall from '@/services/httpsCall';
import logger from '@/utils/logger';
import type { Store } from '@/types/types';

interface CreateStoreInput {
  name: string;
  email: string;
  phone: string;
  status: 'active' | 'inactive';
  subdomain: string;
  store_owner_id: string;
  address: string;
  street: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  manufacturerId: any[];
}

interface UpdateStoreInput {
  storeId: string;
  name: string;
  email: string;
  phone: string;
  status: boolean;
  subdomain: string;
  store_owner_id: string | null;
  address: string;
  street: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  manufacturerId: string[];
}

export interface StoreResponse {
  content?: any[];
  // name: string;
  pages?: any[];
  page: {
    totalPages: number;
    totalElements: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
    paged: boolean;
  };
}

export const fetchAllStores = async ({
  page = 0,
  size = 10,
  keyword = '',
  sortBy = '',
  sortDir = '',
}: {
  page?: number;
  size?: number;
  keyword?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc' | '';
}): Promise<StoreResponse> => {
  try {
    const response = await httpsCall.get(
      `/superadmin/store/list?page=${page}&size=${size}&Keyword=${encodeURIComponent(keyword)}&sortBy=${sortBy}&sortDir=${sortDir}`
    );
    return response.data?.data as StoreResponse;
  } catch (error: any) {
    logger.error('Error fetching owners:', error);
    throw error;
  }
};

export const useFetchAllStores = (
  page: number = 0,
  size: number = 10,
  keyword: string = '',
  sortBy: string = '',
  sortDir: 'asc' | 'desc' | '' = ''
) => {
  return useQuery<StoreResponse, Error>({
    queryKey: ['store', page, size, keyword, sortBy, sortDir],
    queryFn: () => fetchAllStores({ page, size, keyword, sortBy, sortDir }),
    placeholderData: (previousData) => previousData,
  });
};

// * ====================== Fetch Single Store ====================== *
const fetchSingleStoreData = async (id: string): Promise<Store> => {
  const response = await httpsCall.get(`/superadmin/store/${id}`);
  return response?.data?.data as Store;
};

export const useFetchSingleStore = (id?: string | number, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['single-store', id],
    queryFn: () => fetchSingleStoreData(String(id!)),
    // enabled: Boolean(id),
    enabled: !!id && enabled,
    retry: false,
    staleTime: timeConverter(10, 'minute'),
  });
};

// * ====================== Create Store new====================== *
export const createStore = async (storeData: CreateStoreInput) => {
  const response = await httpsCall.post('/superadmin/store/add', {
    name: storeData?.name,
    email: storeData?.email,
    phone: storeData?.phone,
    address: storeData?.address,
    street: storeData?.street,
    city: storeData?.city,
    state: storeData?.state,
    country: storeData?.country,
    zipCode: storeData?.zip,
    active: storeData?.status === 'active' ? true : false,
    storeOwner: { id: storeData?.store_owner_id },
    subDomain: storeData?.subdomain,
    manufacturerIds: storeData?.manufacturerId,
  });
  return response.data;
};

export const useCreateStoreData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createStore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store'], exact: false });
    },
  });
};

// * ====================== Delete Store ====================== *
export const deleteStore = async (storeId: string) => {
  const response = await httpsCall.delete(`/superadmin/store/delete/${storeId}`);
  return response.data;
};

export const useDeleteStore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteStore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store'], exact: false });
    },
  });
};

// * ====================== Update Store ====================== *
export const updateStore = async (updateStoreInput: UpdateStoreInput) => {
  const response = await httpsCall.put(`/superadmin/store/update/${updateStoreInput?.storeId}`, {
    name: updateStoreInput?.name,
    email: updateStoreInput?.email,
    phone: updateStoreInput?.phone,
    address: updateStoreInput?.address,
    street: updateStoreInput?.street,
    city: updateStoreInput?.city,
    state: updateStoreInput?.state,
    country: updateStoreInput?.country,
    zipCode: updateStoreInput?.zip,
    active: updateStoreInput?.status,
    storeOwner: { id: updateStoreInput?.store_owner_id },
    subDomain: updateStoreInput?.subdomain,
    manufacturerIds: updateStoreInput?.manufacturerId,
  });
  return response.data;
};

export const useUpdateStore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateStore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['single-store'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['store'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['unassigned-store-owners'], exact: false });
    },
  });
};

export const useFetchInfiniteStores = (keyword: string, size: number = 10, isEnabled?: boolean) => {
  return useInfiniteQuery<StoreResponse, Error, StoreResponse, ['stores', string], number>({
    queryKey: ['stores', keyword],

    queryFn: ({ pageParam }) => fetchAllStores({ page: pageParam!, size, keyword }),

    getNextPageParam: (lastPage) => (lastPage.page.last ? undefined : lastPage.page.number + 1),

    initialPageParam: 0,

    enabled: isEnabled,
  });
};

// #=============================== single store fetch for owner ===================================

const fetchSingleStoreForOwner = async (id: string): Promise<Store> => {
  const response = await httpsCall.get(`/owner/store/${id}`);
  return response?.data?.data as Store;
};

export const useFetchSingleStoreForOwner = (id?: string) => {
  return useQuery({
    queryKey: ['single-store-owner', id],
    queryFn: () => fetchSingleStoreForOwner(String(id!)),
    enabled: !!id,
    retry: false,
    staleTime: timeConverter(10, 'minute'),
  });
};
